import { apiEndpoints } from "@/src/config/api/endpoints";

import { api } from "./base_service";
import { adaptMatch, mapEntityType, normalizeDob, type SearchResultRaw } from "./screening_service";
import { normalizeDelimitedList } from "./normalize_helpers";
import { ADVERSE_MEDIA_KEYWORD_LABELS } from "./search_configurations_service";
import { addMonitoring, type MonitoringEntry } from "./monitoring_service";
import type { Batch, BatchRow } from "@/app/(app)/_data/batches";
import { batchUploadSchema, type BatchUploadInput } from "@/app/(app)/batch/_schemas/batch-upload";
import type { Match, RiskLevel } from "@/app/(app)/screening/_data/types";
import { isoCountryName } from "@/app/(app)/_lib/iso-countries";
import type { ApiError, Paginated, Result } from "./types";

export type { BatchUploadInput };

type SearchQueryRaw = {
  name?: string;
  min_score?: string;

  data_source?: string | string[];
  country?: string | string[];
  country_residence?: string | string[];
  date_of_birth?: string | string[];
  entity_type?: string;
  identifier?: string;
};

type ParsingErrorRaw = {
  index?: number;
  uploaded_file?: string;
  missing_columns?: string;
  unknown_columns?: string;
  file_error?: string;
  external_identifier?: string | string[];
  data_source?: string[];
  entity_type?: string[];
  name?: string[];
  min_score?: string[];
  country?: string[];
};

type BatchQueryRaw = {
  id: string;
  processed_timestamp?: string;
  processed?: boolean;
  number_of_searches?: number;
  results_count?: number;
  filename?: string;
  url?: string;
  monitoring?: boolean;
  parsing_errors?: ParsingErrorRaw[] | null;
};

/** A watchlist inside-result is the same DataEntry serializer Single Screening
 *  reads (SearchResultRaw), so it is adapted by the same `adaptMatch`. Country
 *  fields are loosened because this endpoint has echoed them as plain strings;
 *  the rest are the Adverse Media article fields. */
type InsideResultRaw = Partial<
  Omit<SearchResultRaw, "nationality" | "country_residence" | "citizenship">
> & {
  nationality?: string | string[];
  country_residence?: string | string[];
  citizenship?: string | string[];
  title?: string;
  description?: string;
  published_at?: string;
  country?: string;
  language?: string;
  url?: string;
  media_source?: { name?: string; url?: string };
  keywords?: string[];
  individuals?: string[];
  entities?: string[];
  image?: string[];
  body?: string;
};

type BatchResultRaw = {
  id: string;
  external_identifier?: string;
  search_query_json?: SearchQueryRaw;
  results_count?: number;
  url?: string;
  results?: {
    count?: number;
    next?: string | null;
    previous?: string | null;
    results?: InsideResultRaw[];
  };
};

type BatchDetailRaw = {
  id: string;
  timestamp?: string;
  processed?: boolean;
  number_of_searches?: number;
  filename?: string;
  results_count?: number;
  batch_results?: BatchResultRaw[];
  message?: string;
  file_urls?: { zip_results_url?: string; json_results_url?: string };
  parsing_errors?: ParsingErrorRaw[] | null;
};

const DEFAULT_FILENAME = "Batch upload";
const MAX_PAGES = 50;
const filenameCache = new Map<string, string>();

/** Forget cached batch filenames (logout) so they never leak into another session. */
export function clearBatchFilenameCache(): void {
  filenameCache.clear();
}

function asString(v: unknown): string {
  if (typeof v === "string") return v;
  if (v == null) return "";
  return String(v);
}
function asNum(v: unknown): number {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}
function pageRows<T>(p: Paginated<T> | undefined): T[] {
  return Array.isArray(p?.results) ? p.results : [];
}

/** Turns the raw `parsing_errors` entries into human-readable messages. */
function flattenParsingErrors(parsingErrors: ParsingErrorRaw[]): string[] {
  const onlyUploadedFileErrors = parsingErrors.every(
    (error) => error.uploaded_file && !error.missing_columns && !error.unknown_columns,
  );
  if (onlyUploadedFileErrors) {
    return ["Could not be processed, please use our template"];
  }

  const flattenErrors: string[] = [
    "This file doesn’t match the expected format. Errors were found in these fields:",
  ];
  const hasDataSource = parsingErrors.some((error) => error.data_source);
  const hasEntityType = parsingErrors.some((error) => error.entity_type);
  const hasFileError = parsingErrors.some((error) => error.file_error);
  const hasNameError = parsingErrors.some((error) => error.name);
  const hasMinScoreError = parsingErrors.some((error) => error.min_score);
  const hasExternalIdentifierError = parsingErrors.some((error) =>
    Array.isArray(error.external_identifier),
  );
  const hasCountryError = parsingErrors.some((error) => error.country);

  parsingErrors.forEach((error) => {
    if (error.missing_columns) {
      flattenErrors.push(`Missing columns: ${error.missing_columns}`);
    }
    if (error.unknown_columns) {
      flattenErrors.push(`Unknown columns: ${error.unknown_columns}`);
    }
    if (error.uploaded_file) {
      flattenErrors.push("Could not be processed, please use our template");
    }
  });

  const fieldErrors: string[] = [];
  if (hasDataSource) fieldErrors.push("data source");
  if (hasEntityType) fieldErrors.push("entity type");
  if (hasNameError) fieldErrors.push("full name");
  if (hasMinScoreError) fieldErrors.push("minimum score");
  if (hasExternalIdentifierError) fieldErrors.push("external identifier");
  if (hasCountryError) fieldErrors.push("country");
  if (hasFileError) fieldErrors.push("file error");

  if (fieldErrors.length) flattenErrors.push(`${fieldErrors.join(", ")}.`);
  return flattenErrors;
}

function parsingErrorMessages(raw: ParsingErrorRaw[] | null | undefined): string[] | undefined {
  return raw?.length ? flattenParsingErrors(raw) : undefined;
}

/** A LIST row → a Batch summary (no per-row data at list level). */
export function adaptListBatch(raw: BatchQueryRaw): Batch {
  const id = asString(raw.id);
  if (raw.filename) filenameCache.set(id, raw.filename);
  return {
    id,
    filename: raw.filename || DEFAULT_FILENAME,
    uploadedAt: asString(raw.processed_timestamp).slice(0, 10),
    rows: [],
    nameCount: asNum(raw.number_of_searches ?? raw.results_count),
    processing: raw.processed === false,
    parsingErrorMessages: parsingErrorMessages(raw.parsing_errors),
  };
}

export function adaptDetailRow(raw: BatchResultRaw): BatchRow {
  const q = raw.search_query_json ?? {};
  const rawEntityType = asString(q.entity_type);
  return {
    id: asString(raw.id),
    externalId: asString(raw.external_identifier),
    minScore: asNum(q.min_score),
    dataSource: normalizeDelimitedList(q.data_source).map((s) => s.toUpperCase()),
    name: asString(q.name),
    dateOfBirth: normalizeDob(q.date_of_birth),
    country: normalizeDelimitedList(q.country ?? q.country_residence),
    entityType: rawEntityType ? mapEntityType(rawEntityType) : "Individual",
    identifier: q.identifier ? asString(q.identifier) : null,
    hits: asNum(raw.results_count ?? raw.results?.count),
    resultsUrl: raw.url || undefined,
  };
}

function riskFromScore(score: number): RiskLevel {
  if (score >= 0.9) return "critical";
  if (score >= 0.8) return "high";
  if (score >= 0.6) return "medium";
  return "low";
}

export function adaptInsideResult(raw: InsideResultRaw, row: BatchRow): Match {
  const raw0 = raw.confidence_score;
  const score = typeof raw0 === "number" ? (raw0 > 1 ? raw0 / 100 : raw0) : 0;
  const isAdverseMedia =
    row.dataSource.includes("ADV-MEDIA") || asString(raw.entity_type) === "Media";

  if (isAdverseMedia) {
    const countryIso = asString(
      raw.country ??
        normalizeDelimitedList(raw.country_residence)[0] ??
        normalizeDelimitedList(raw.nationality)[0],
    );
    const country = countryIso ? isoCountryName(countryIso) || countryIso : "";
    const fullTitle = asString(raw.title ?? raw.name);
    const additionalInformation: Record<string, string> = {};
    if (fullTitle.length > 100) additionalInformation["Full Title"] = fullTitle;
    if (raw.url) additionalInformation["Article URL"] = raw.url;
    if (raw.media_source?.name) additionalInformation["Media Source"] = raw.media_source.name;
    if (raw.media_source?.url) additionalInformation["Source URL"] = raw.media_source.url;
    if (raw.keywords?.length) {
      additionalInformation["Keywords"] = raw.keywords
        .map((k) => ADVERSE_MEDIA_KEYWORD_LABELS[k] ?? k)
        .join(", ");
    }
    if (raw.individuals?.length) additionalInformation["Individuals"] = raw.individuals.join(", ");
    if (raw.entities?.length) additionalInformation["Entities"] = raw.entities.join(", ");
    if (raw.language) additionalInformation["Language"] = raw.language;
    if (raw.image?.[0]) additionalInformation["Image"] = raw.image[0];

    return {
      id: asString(raw.si_identifier) || asString(raw.url) || fullTitle,
      score,
      name: fullTitle.length > 100 ? `${fullTitle.slice(0, 100)}...` : fullTitle,
      type: "Media",
      country,
      countryIso: countryIso || undefined,
      dob: null,
      source: "ADV-MEDIA",
      collection: "adverse_media",
      firstImported: asString(raw.first_import).slice(0, 10) || undefined,
      lastUpdated: asString(raw.published_at ?? raw.last_update).slice(0, 10),
      mediaSource: raw.media_source?.name || undefined,
      riskLevel: riskFromScore(score),
      alternativeNames: [],
      knownLocations: country ? [{ label: "Country", value: country }] : [],
      remarks: raw.description ? [raw.description] : [],
      additionalInformation,
      articleBody: raw.body || undefined,
    };
  }

  // Watchlist results must render exactly like Single Screening (aliases,
  // countries, known locations, collection badge…), so delegate to its adapter
  // and only override what is batch-specific.
  const match = adaptMatch({
    ...raw,
    name: asString(raw.name),
    confidence_score: score,
    country_residence: normalizeDelimitedList(raw.country_residence),
    nationality: normalizeDelimitedList(raw.nationality),
    citizenship: normalizeDelimitedList(raw.citizenship),
  });
  const rawEntityType = asString(raw.entity_type);
  return {
    ...match,
    // Prefixed with the row id: the same listing can match several batch rows.
    id: `${row.id}-${asString(raw.si_identifier) || asString(raw.name)}`,
    type: rawEntityType ? mapEntityType(rawEntityType) : row.entityType,
  };
}

export async function getLatestBatchId(): Promise<Result<string>> {
  try {
    const page = await api.get<Paginated<BatchQueryRaw>>(apiEndpoints.batch().list());
    const id = asString(pageRows(page)[0]?.id);
    if (!id)
      return {
        ok: false,
        error: { status: 404, code: "not_found", message: "No batches found", requestId: "client" },
      };
    return { ok: true, value: id };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

export async function listBatches(): Promise<Result<Batch[]>> {
  try {
    const all: Batch[] = [];
    let page = await api.get<Paginated<BatchQueryRaw>>(apiEndpoints.batch().list());
    all.push(...pageRows(page).map(adaptListBatch));
    let next = typeof page?.next === "string" ? page.next : null;
    let guard = 0;
    while (next && guard < MAX_PAGES) {
      page = await api.get<Paginated<BatchQueryRaw>>(next);
      all.push(...pageRows(page).map(adaptListBatch));
      next = typeof page?.next === "string" ? page.next : null;
      guard += 1;
    }
    return { ok: true, value: all };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

export async function getBatch(id: string): Promise<Result<Batch>> {
  try {
    const data = await api.get<BatchDetailRaw>(apiEndpoints.batch().detail(id));
    if (data?.filename) filenameCache.set(id, data.filename);
    if (!filenameCache.has(id)) {
      await listBatches();
    }
    const filename = filenameCache.get(id) || DEFAULT_FILENAME;

    const isProcessing =
      data?.processed === false || (typeof data?.message === "string" && !data.batch_results);
    if (isProcessing) {
      return {
        ok: true,
        value: {
          id,
          filename,
          uploadedAt: asString(data?.timestamp).slice(0, 10),
          nameCount: asNum(data?.number_of_searches ?? data?.results_count),
          rows: [],
          processing: true,
          parsingErrorMessages: parsingErrorMessages(data?.parsing_errors),
        },
      };
    }
    const rows = (data?.batch_results ?? []).map(adaptDetailRow);
    return {
      ok: true,
      value: {
        id: asString(data?.id) || id,
        filename,
        uploadedAt: asString(data?.timestamp).slice(0, 10),
        rows,
        nameCount: asNum(data?.number_of_searches ?? data?.results_count) || rows.length,
        downloadUrl: data?.file_urls?.zip_results_url || undefined,
        parsingErrorMessages: parsingErrorMessages(data?.parsing_errors),
      },
    };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

const ROW_MATCHES_PAGE_SIZE = 100;

export type BatchRowMatchesPage = { matches: Match[]; count: number; next: string | null };

/** One page of a batch row's matches. `row.resultsUrl` is the absolute URL
 *  the batch-detail payload gave for this row — a row is either entirely
 *  Adverse Media or entirely another data source (`row.dataSource`), never
 *  mixed, so callers pick their column set once from that, not per-row. */
export async function getBatchRowMatches(
  row: BatchRow,
  page = 1,
): Promise<Result<BatchRowMatchesPage>> {
  try {
    if (!row.resultsUrl) return { ok: true, value: { matches: [], count: 0, next: null } };
    const data = await api.get<BatchResultRaw>(row.resultsUrl, {
      params: { page, page_size: ROW_MATCHES_PAGE_SIZE },
    });
    const inside = data?.results?.results ?? [];
    return {
      ok: true,
      value: {
        matches: inside.map((r) => adaptInsideResult(r, row)),
        count: data?.results?.count ?? inside.length,
        next: data?.results?.next ?? null,
      },
    };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

export async function addBatchRowToMonitoring(row: BatchRow): Promise<Result<MonitoringEntry>> {
  return addMonitoring({
    name: row.name,
    externalIdentifier: row.externalId || undefined,
    identifier: row.identifier || undefined,
    dob: row.dateOfBirth,
    countries: row.country.length > 0 ? row.country : undefined,
    entityType: row.entityType,
    watchlists: row.dataSource.length > 0 ? row.dataSource : undefined,
    confidenceScore: Math.round(row.minScore * 100),
  });
}

export async function uploadBatch(input: BatchUploadInput): Promise<Result<Batch>> {
  const parsed = batchUploadSchema.safeParse(input);
  if (!parsed.success) {
    return {
      ok: false,
      error: {
        status: 422,
        code: "validation",
        message: parsed.error.issues[0]?.message ?? "Invalid file",
        requestId: "client",
      },
    };
  }
  try {
    const form = new FormData();
    form.append("batch_query_file", parsed.data.file);
    const data = await api.post<BatchQueryRaw & BatchDetailRaw>(
      apiEndpoints.batch().upload(),
      form,
    );
    return { ok: true, value: adaptUploadResponse(data, parsed.data.file.name) };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

function adaptUploadResponse(
  data: (BatchQueryRaw & BatchDetailRaw) | undefined,
  fallbackName: string,
): Batch {
  const d = data ?? ({} as BatchQueryRaw & BatchDetailRaw);
  const id = asString(d.id);
  const filename = d.filename || fallbackName;
  filenameCache.set(id, filename);
  return {
    id,
    filename,
    uploadedAt: asString(d.processed_timestamp ?? d.timestamp).slice(0, 10),
    rows: [],
    nameCount: asNum(d.results_count),
    processing: d.processed === false || (!!d.message && !d.batch_results),
    parsingErrorMessages: parsingErrorMessages(d.parsing_errors),
  };
}

export async function removeBatch(id: string): Promise<Result<void>> {
  try {
    await api.delete<void>(apiEndpoints.batch().delete(id));
    return { ok: true, value: undefined };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

export async function clearBatches(): Promise<Result<void>> {
  try {
    const list = await listBatches();
    if (!list.ok) return list;
    for (const b of list.value) {
      await api.delete<void>(apiEndpoints.batch().delete(b.id));
    }
    return { ok: true, value: undefined };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}
